import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { Place } from '@polaris-customerweb/sharedcomponents.map';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { isEmpty } from 'lodash-es';
import type { Point } from 'mapbox-gl';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
    selectActivePointOfInterest,
    selectActivePremiumPoi,
} from 'src/app/components/point-of-interest/point-of-interest.reducer';
import { AppState } from 'src/app/models/app-state';
import {
    selectIsShowingFood,
    selectIsShowingFuel,
    selectIsShowingLodging,
    selectIsShowingUnknown,
} from '../state/map/layer-visibility/layer-visibility.reducer';
import { MapExtents } from '../state/map/map.reducer';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { PlaceService } from './place.service';

@Injectable({ providedIn: 'root' })
export class PointOfInterestMapService extends MapContentMapService {
    static minZoom = 10;

    constructor(
        private configService: ConfigService,
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private placeService: PlaceService,
        private store: Store<AppState>
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('pois');
    }

    addSources(mapboxMap: MapboxMap): void {
        const layerIds = this.mapService.getLayerIds(mapboxMap);
        this.getLayerIds(layerIds);
        this.createLayerSources(mapboxMap);

        this.layerIds.forEach((layerId) => {
            this.mapService.replaceLayer(mapboxMap, layerId, this.sourceId);
        });

        this._layerIdsSelectable = [
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];

        const minZoom = this.getMinZoomFromStyle(mapboxMap);
        if (minZoom !== undefined) {
            this.minZoom = minZoom;
        }

        // addSources may get called multiple times in the lifetime of a map.
        // unset initial selected ID for future calls.
        this.initialSelectedIds = undefined;
    }

    setSourceDataFromModel(mapboxMap: MapboxMap, pointsOfInterest: Place[]) {
        const geoJson = pointsOfInterest
            ? this.placeService.placesToGeoJson(pointsOfInterest)
            : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    getPointOfInterestAtPoint(
        mapboxMap: MapboxMap,
        point: Point
    ): Place | undefined {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        let poi: Place | undefined;

        if (!isEmpty(features)) {
            poi = this.placeService.getByIdFromCache(
                features[0].properties?.id as string
            );
        }

        return poi;
    }

    setLabelVisibility(mapboxMap: MapboxMap, isVisible: boolean) {
        const opacity = isVisible ? 1 : 0;
        this.layerIdsUnselected.forEach((layerId) => {
            this.mapService.setPaintProperty(
                mapboxMap,
                layerId,
                'text-opacity',
                opacity
            );
        });
    }

    /**
     * @description Set places layer data when isShowingPlaces changes to true and when map is moved while isShowingPlaces is true
     * @param mapExtents$ map extents observable.
     */
    updatePlacesWhenMapExtentsChanges(
        mapExtents$: Observable<MapExtents>
    ): Observable<Place[]> {
        const filteredMapExtents$ = mapExtents$.pipe(
            filter(
                (mapExtents) =>
                    Boolean(mapExtents) && Boolean(mapExtents.bounds)
            )
        );

        // For more than six elements in a combineLatest we have to manually type
        // https://github.com/ReactiveX/rxjs/issues/3601#issuecomment-384711601

        return combineLatest([
            this.store.pipe(select(selectIsShowingLodging)),
            this.store.pipe(select(selectIsShowingFood)),
            this.store.pipe(select(selectIsShowingFuel)),
            this.store.pipe(select(selectIsShowingUnknown)),
            this.store.pipe(select(selectActivePointOfInterest)),
            this.store.pipe(select(selectActivePremiumPoi)),
            filteredMapExtents$,
        ]).pipe(
            switchMap(
                ([
                    isLodging,
                    isFood,
                    isFuel,
                    isUnknown,
                    activePoi,
                    activePremiumPoi,
                    extents,
                ]: [
                    boolean,
                    boolean,
                    boolean,
                    boolean,
                    Place | undefined,
                    Place | undefined,
                    MapExtents,
                ]) => {
                    const includeStandard =
                        extents.zoom >= PointOfInterestMapService.minZoom;
                    const includeSponsors = this.configService.isOffRoad;
                    const activePoiOrPremiumPoi = activePoi || activePremiumPoi;
                    const excludedTypes: string[] = [];
                    if (!isLodging) {
                        excludedTypes.push('Lodging');
                    }
                    if (!isFood) {
                        excludedTypes.push('Food');
                    }
                    if (!isFuel) {
                        excludedTypes.push('Fuel');
                    }
                    if (!isUnknown) {
                        excludedTypes.push('Unknown');
                        excludedTypes.push('Medical');
                    }

                    if (!includeStandard && !includeSponsors) {
                        const pois = activePoiOrPremiumPoi
                            ? [activePoiOrPremiumPoi]
                            : [];
                        return of(pois);
                    }

                    const maxPois = this.configService.maxPoiResults;
                    return this.placeService
                        .getByBounds(
                            extents.bounds,
                            includeStandard,
                            excludedTypes,
                            includeSponsors,
                            maxPois,
                            extents.zoom
                        )
                        .pipe(
                            map((poisInView) => {
                                if (!activePoiOrPremiumPoi) {
                                    return poisInView;
                                }

                                return [
                                    ...poisInView.filter(
                                        (poi) =>
                                            poi.id !== activePoiOrPremiumPoi.id
                                    ),
                                    activePoiOrPremiumPoi,
                                ];
                            })
                        );
                }
            )
        );
    }
}
